import React, { useState, useRef, useLayoutEffect, useEffect } from 'react';
import './FilterPopover.css';
import { useLanguage } from '../contexts/LanguageContext';
import { useBodyScrollLock } from '../hooks';
import {
  popoverButtonContainerStyle,
  popoverCancelButtonStyle,
  popoverSubmitButtonStyle
} from './shared/PopoverStyles';

// Issue #179: Accept availablePrograms as prop for dynamic study programs per university
function FilterPopover({ onClose, onApply, initialFilters, availablePrograms = [] }) {
  const { t } = useLanguage();
  // Filter state
  const [degree, setDegree] = useState(initialFilters.degree || '');
  const [selectedPrograms, setSelectedPrograms] = useState(initialFilters.study_programs || []);
  const [language, setLanguage] = useState(initialFilters.language || '');
  const [semester, setSemester] = useState(initialFilters.semester || '');
  const [programSearch, setProgramSearch] = useState('');
  // Issue #78: Rating-related filters
  const [hasReviews, setHasReviews] = useState(initialFilters.has_reviews || false);
  const [myReviews, setMyReviews] = useState(initialFilters.my_reviews || false);
  // Issue #78: "Mindestens [x] Sterne" is a toggle button; the star selector is only
  // activated once the button is enabled. minRating holds the chosen threshold (0 = none).
  const [minRatingEnabled, setMinRatingEnabled] = useState((initialFilters.min_rating || 0) > 0);
  const [minRating, setMinRating] = useState(initialFilters.min_rating || 0);
  // Validation flag: set when the user enables the filter but applies without picking stars.
  const [minRatingError, setMinRatingError] = useState(false);

  // Issue #78: Lock the background page while the popover is open so the course
  // review page underneath cannot scroll, move or be interacted with.
  useBodyScrollLock(true);

  // Issue #78: Freeze the scrollable body to its initial (default) content height so
  // the popover opens exactly as tall as its content needs - no reserved/empty space -
  // and then never grows when filters are added (e.g. selecting study programs or
  // activating the star rating). Once frozen, extra content scrolls inside this height
  // while the header and footer stay pinned.
  const contentRef = useRef(null);
  const ratingRef = useRef(null);
  const [bodyMaxHeight, setBodyMaxHeight] = useState(null);
  useLayoutEffect(() => {
    if (contentRef.current) {
      // Measured before any max-height is applied, so this is the natural height of
      // the default content. +1px avoids a sub-pixel rounding scrollbar at rest.
      setBodyMaxHeight(contentRef.current.scrollHeight + 1);
    }
  }, []);

  // Issue #78: When the "Mindestens [x] Sterne" filter is activated (or its validation
  // error appears), the stars/error sit below the fold of the frozen body. Auto-scroll
  // them into view so the user is hinted to pick a star rating - and sees the error -
  // without having to discover the hidden content by scrolling manually.
  // Respects prefers-reduced-motion (no smooth animation when the user opts out).
  useEffect(() => {
    if (minRatingEnabled && ratingRef.current) {
      const prefersReducedMotion = window.matchMedia
        && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      ratingRef.current.scrollIntoView({
        behavior: prefersReducedMotion ? 'auto' : 'smooth',
        block: 'nearest',
      });
    }
  }, [minRatingEnabled, minRatingError]);

  // Toggle the "Mindestens [x] Sterne" filter on/off. Turning it off clears any
  // selected stars and the validation error.
  const toggleMinRatingEnabled = () => {
    setMinRatingEnabled(prev => {
      const next = !prev;
      if (!next) {
        setMinRating(0);
        setMinRatingError(false);
      }
      return next;
    });
  };

  // Select a minimum star threshold (clicking the same value again clears it).
  const selectMinRating = (star) => {
    setMinRating(prev => (prev === star ? 0 : star));
    setMinRatingError(false);
  };

  // Filter programs based on search
  const filteredPrograms = availablePrograms.filter(program =>
    program.toLowerCase().includes(programSearch.toLowerCase())
  );

  const toggleProgram = (program) => {
    if (selectedPrograms.includes(program)) {
      setSelectedPrograms(selectedPrograms.filter(p => p !== program));
    } else {
      setSelectedPrograms([...selectedPrograms, program]);
    }
    setProgramSearch(''); // Clear search after selection
  };

  const removeProgram = (program) => {
    setSelectedPrograms(selectedPrograms.filter(p => p !== program));
  };

  const handleApply = () => {
    // Issue #78: If the user enabled the "Mindestens [x] Sterne" filter but did not
    // pick a star rating, signal the missing selection (red stars) and block applying.
    if (minRatingEnabled && minRating === 0) {
      setMinRatingError(true);
      return;
    }

    onApply({
      degree,
      study_programs: selectedPrograms,
      language,
      semester,
      // Issue #78: Rating-related filters
      has_reviews: hasReviews,
      my_reviews: myReviews,
      min_rating: minRatingEnabled ? minRating : 0
    });
    onClose();
  };

  const handleCancel = () => {
    onClose();
  };

  // Issue #75: handleClear removed - reset button moved outside popover to CourseReview.js

  return (
    <div className="filter-popover-overlay" onClick={handleCancel}>
      <div className="filter-popover" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="filter-popover-header">
          <span className="filter-icon" aria-hidden="true">⚙️</span>
          <span className="filter-title">{t('courseReview.filter.title')}</span>
        </div>

        {/* Content */}
        <div
          className="filter-popover-content"
          ref={contentRef}
          style={bodyMaxHeight ? { maxHeight: `${bodyMaxHeight}px` } : undefined}
        >
          {/* Studium (Degree) */}
          <div className="filter-section">
            <div className="filter-section-label">{t('courseReview.filter.degree')}</div>
            <div className="filter-chips filter-chips-degree">
              <button
                className={`filter-chip ${degree === 'Bachelor' ? 'selected' : ''}`}
                onClick={() => setDegree(degree === 'Bachelor' ? '' : 'Bachelor')}
              >
                Bachelor
              </button>
              <button
                className={`filter-chip ${degree === 'Master' ? 'selected' : ''}`}
                onClick={() => setDegree(degree === 'Master' ? '' : 'Master')}
              >
                Master
              </button>
            </div>
          </div>

          {/* Studiengang (Study Program) */}
          <div className="filter-section">
            <div className="filter-section-label">{t('courseReview.filter.studyProgram')}</div>

            {/* Search Bar */}
            <div className="filter-search-bar">
              <input
                type="text"
                className="filter-search-input"
                placeholder={t('courseReview.filter.searchProgram')}
                value={programSearch}
                onChange={(e) => setProgramSearch(e.target.value)}
              />
              <span className="filter-search-icon">🔍</span>
            </div>

            {/* Selected Programs */}
            {selectedPrograms.length > 0 && (
              <div className="filter-selected-programs">
                {selectedPrograms.map(program => (
                  <div key={program} className="filter-chip-selected">
                    <span title={program}>{program}</span>
                    <button
                      className="filter-chip-remove"
                      onClick={() => removeProgram(program)}
                      aria-label="Remove"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Search Results */}
            {programSearch && filteredPrograms.length > 0 && (
              <div className="filter-search-results">
                {filteredPrograms.map(program => (
                  <button
                    key={program}
                    className="filter-search-result-item"
                    onClick={() => toggleProgram(program)}
                  >
                    {program}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Sprache (Language) */}
          <div className="filter-section">
            <div className="filter-section-label">{t('courseReview.filter.language')}</div>
            <div className="filter-chips filter-chips-language">
              <button
                className={`filter-chip ${language === 'Deutsch' ? 'selected' : ''}`}
                onClick={() => setLanguage(language === 'Deutsch' ? '' : 'Deutsch')}
              >
                {t('courseReview.filter.german')}
              </button>
              <button
                className={`filter-chip ${language === 'Englisch' ? 'selected' : ''}`}
                onClick={() => setLanguage(language === 'Englisch' ? '' : 'Englisch')}
              >
                {t('courseReview.filter.english')}
              </button>
              <button
                className={`filter-chip ${language === 'Beides' ? 'selected' : ''}`}
                onClick={() => setLanguage(language === 'Beides' ? '' : 'Beides')}
              >
                {t('courseReview.filter.both')}
              </button>
            </div>
          </div>

          {/* Semester */}
          <div className="filter-section">
            <div className="filter-section-label">{t('courseReview.filter.semester')}</div>
            <div className="filter-chips filter-chips-semester">
              <button
                className={`filter-chip ${semester === 'Wintersemester' ? 'selected' : ''}`}
                onClick={() => setSemester(semester === 'Wintersemester' ? '' : 'Wintersemester')}
              >
                {t('courseReview.filter.winterSemester')}
              </button>
              <button
                className={`filter-chip ${semester === 'Sommersemester' ? 'selected' : ''}`}
                onClick={() => setSemester(semester === 'Sommersemester' ? '' : 'Sommersemester')}
              >
                {t('courseReview.filter.summerSemester')}
              </button>
            </div>
          </div>

          {/* Issue #78: Bewertung (Rating) filters */}
          <div className="filter-section">
            <div className="filter-section-label">{t('courseReview.filter.ratingSection')}</div>

            {/* Issue #78: Three toggle chips. "Mindestens [x] Sterne" is a button just
                like the other two; the star selector only appears once it is enabled. */}
            <div className="filter-chips filter-chips-rating">
              <button
                type="button"
                className={`filter-chip ${hasReviews ? 'selected' : ''}`}
                aria-pressed={hasReviews}
                onClick={() => setHasReviews(!hasReviews)}
              >
                {t('courseReview.filter.hasReviews')}
              </button>
              <button
                type="button"
                className={`filter-chip ${myReviews ? 'selected' : ''}`}
                aria-pressed={myReviews}
                onClick={() => setMyReviews(!myReviews)}
              >
                {t('courseReview.filter.myReviews')}
              </button>
              <button
                type="button"
                className={`filter-chip ${minRatingEnabled ? 'selected' : ''}`}
                aria-pressed={minRatingEnabled}
                aria-expanded={minRatingEnabled}
                aria-controls="filter-min-rating-section"
                onClick={toggleMinRatingEnabled}
              >
                {t('courseReview.filter.minStars')}
              </button>
            </div>

            {/* Star selector - only activated when the toggle button is enabled.
                Issue #78: When activated the body auto-scrolls here (see scroll effect)
                so the user sees the stars/validation hint without scrolling manually. */}
            {minRatingEnabled && (
              <div className="filter-min-rating" id="filter-min-rating-section" ref={ratingRef}>
                <div
                  className={`filter-stars ${minRatingError ? 'error' : ''}`}
                  role="group"
                  aria-label={t('courseReview.filter.minStars')}
                  aria-describedby={minRatingError ? 'filter-min-rating-error' : undefined}
                >
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      type="button"
                      className={`filter-star-button ${star <= minRating ? 'filled' : 'empty'}`}
                      aria-pressed={minRating === star}
                      aria-label={t('courseReview.filter.minStarsAria').replace('{count}', star)}
                      onClick={() => selectMinRating(star)}
                    >
                      <svg width="26" height="26" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                        <path
                          d="M15 2.5L18.09 11.26L27.45 12.36L21.23 18.32L22.68 27.64L15 23.09L7.32 27.64L8.77 18.32L2.55 12.36L11.91 11.26L15 2.5Z"
                          fill={star <= minRating ? '#F6B707' : 'none'}
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                  ))}
                </div>
                {minRatingError && (
                  <div className="filter-min-rating-error" id="filter-min-rating-error" role="alert">
                    {t('courseReview.filter.minStarsRequired')}
                  </div>
                )}
              </div>
            )}
          </div>

        </div>

        {/* Issue #78: Pinned footer - the button group (Abbrechen/Anwenden) stays
            visible at the bottom while the content above scrolls. Only the content
            region is affected by the scroll bar. */}
        {/* Issue #75: "Filter zurücksetzen" lives outside the popover */}
        <div className="filter-popover-footer">
          <div style={popoverButtonContainerStyle}>
            <button
              onClick={handleCancel}
              style={popoverCancelButtonStyle}
            >
              {t('cancel')}
            </button>
            <button
              onClick={handleApply}
              style={popoverSubmitButtonStyle}
            >
              {t('courseReview.filter.apply')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default FilterPopover;
